<ng-template
	#MigrationProgressModalTemplate
	class="modal fade"
	tabindex="-1"
	role="dialog"
	aria-labelledby="migration-progress-modal-label">
	<div class="modal-content">
		<div class="modal-header">
			<div class="col-2">
				<i class="fa-solid fa-spinner fa-spin fa-xl" *ngIf="isRunning"></i>
			</div>
			<div class="col-8">
				<h2 class="modal-title text-center" id="migration-progress-modal-label">
					{{ partLabel }} - {{ clientName }}
				</h2>
				<div
					*ngIf="batchTotal > 1"
					class="text-center text-muted"
					data-testid="migration-progress-batch-position">
					Carrier {{ batchIndex }} of {{ batchTotal }}
				</div>
			</div>
			<div class="col-2 text-right">
				<button
					class="btn btn-outline-dark"
					[disabled]="isRunning"
					[attr.title]="isRunning ? 'Available when the migration finishes' : null"
					(click)="close()"
					data-testid="migration-progress-modal-close">
					Close
				</button>
			</div>
		</div>
		<div class="modal-body">
			<div class="d-flex justify-content-between align-items-center mb-2">
				<div>
					<span
						class="badge migration-status-badge mr-2"
						[ngClass]="overallStatusClass"
						data-testid="migration-progress-status">
						{{ overallStatus }}
					</span>
					<span class="text-success mr-3">{{ successCount }} succeeded</span>
					<span class="text-danger mr-3">{{ failedCount }} failed</span>
					<span class="text-muted">{{ skippedCount }} skipped</span>
				</div>
				<div class="text-muted" data-testid="migration-progress-elapsed">
					Elapsed <strong>{{ formatDuration(elapsedMs) }}</strong>
				</div>
			</div>

			<div class="progress mb-3 migration-progress-bar">
				<div
					class="progress-bar"
					role="progressbar"
					[ngClass]="{
						'progress-bar-striped progress-bar-animated': isRunning,
						'bg-success': !isRunning && overallStatus === 'Completed',
						'bg-warning': !isRunning && overallStatus === 'Completed with problems',
						'bg-danger': !isRunning && overallStatus === 'Failed'
					}"
					[style.width.%]="overallPercent">
					{{ stepsFinished }} / {{ stepRows.length }} steps
				</div>
			</div>

			<div *ngIf="isRunning && progress" class="mb-3" data-testid="migration-progress-activity">
				<div class="text-primary">
					<i class="fa-solid fa-spinner fa-spin mr-1"></i>
					Step {{ progress.currentStep }}: {{ progress.activity }}
					<span *ngIf="progress.total" class="text-muted">
						({{ progress.completed }} of {{ progress.total }})
					</span>
				</div>
				<div class="progress mt-1 migration-item-bar" *ngIf="progress.total">
					<div class="progress-bar bg-info" role="progressbar" [style.width.%]="itemPercent"></div>
				</div>
			</div>

			<div
				*ngIf="!isRunning && summary"
				class="alert mb-3"
				[ngClass]="overallStatus === 'Completed' ? 'alert-success' : overallStatus === 'Failed' ? 'alert-danger' : 'alert-warning'"
				data-testid="migration-progress-summary">
				{{ summary }}
			</div>

			<div class="mb-3" *ngIf="!isRunning && exceptionCount > 0">
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					(click)="downloadExceptions()"
					data-testid="migration-progress-download-exceptions">
					<i class="fa-solid fa-download mr-1"></i>Download {{ exceptionCount }} exception(s)
				</button>
			</div>
			<div class="mb-3" *ngIf="!isRunning && skippedInvoiceCount > 0">
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					(click)="downloadSkippedInvoices()"
					data-testid="migration-progress-download-negative">
					<i class="fa-solid fa-download mr-1"></i>Download {{ skippedInvoiceCount }} skipped negative invoice(s) for the account rep
				</button>
			</div>
			<div class="mb-3" *ngIf="!isRunning && invoiceImportErrorFiles.length > 0">
				<button
					*ngFor="let errorFile of invoiceImportErrorFiles; let errorFileIndex = index"
					type="button"
					class="btn btn-sm mr-2 mb-1"
					[ngClass]="isSummaryFile(errorFile) ? 'btn-outline-primary' : 'btn-outline-danger'"
					[disabled]="downloadingErrorFile === errorFile"
					(click)="downloadInvoiceImportErrors(errorFile)"
					[attr.data-testid]="'migration-progress-download-invoice-import-errors-' + errorFileIndex">
					<i class="fa-solid mr-1" [ngClass]="downloadingErrorFile === errorFile ? 'fa-spinner fa-spin' : 'fa-download'"></i>Download
					{{ invoiceFileLabel(errorFile) }} ({{ errorFile }})
				</button>
				<div class="small text-danger" *ngIf="downloadErrorMessage" data-testid="migration-progress-download-error">{{ downloadErrorMessage }}</div>
			</div>

			<table class="table table-sm mb-0">
				<thead>
					<tr>
						<th class="text-center" style="width: 50px">Step</th>
						<th style="width: 320px">Action</th>
						<th style="width: 120px">Status</th>
						<th class="text-right" style="width: 70px">Time</th>
						<th>Detail</th>
					</tr>
				</thead>
				<tbody>
					<tr
						*ngFor="let row of stepRows"
						[ngClass]="{ 'table-active': row.status === 'Running' }"
						[attr.data-testid]="'migration-progress-step-' + row.step">
						<td class="text-center">{{ row.step }}</td>
						<td>{{ row.action }}</td>
						<td [ngClass]="statusClass(row.status)">
							<i class="fa-solid mr-1" [ngClass]="statusIcon(row.status)"></i>
							{{ row.status }}
						</td>
						<td class="text-right text-muted">{{ formatDuration(row.durationMs) }}</td>
						<td class="migration-step-detail">
							<div class="migration-step-note" *ngFor="let line of detailLines(row.detail)">{{ line }}</div>
						</td>
					</tr>
				</tbody>
			</table>

			<div *ngIf="batchTotal > 1 && batchEntries.length > 0" class="mt-4" data-testid="migration-progress-batch">
				<h5 class="mb-2">Finished in this batch ({{ batchEntries.length }} of {{ batchTotal }})</h5>
				<table class="table table-sm mb-0">
					<thead>
						<tr>
							<th style="width: 260px">Carrier</th>
							<th style="width: 200px">Status</th>
							<th>Summary</th>
						</tr>
					</thead>
					<tbody>
						<tr
							*ngFor="let entry of batchEntries; let entryIndex = index"
							[attr.data-testid]="'migration-progress-batch-' + entryIndex">
							<td>{{ entry.clientName }}</td>
							<td [ngClass]="batchStatusClass(entry.status)">
								<i class="fa-solid mr-1" [ngClass]="batchStatusIcon(entry.status)"></i>
								{{ entry.status }}
							</td>
							<td class="migration-step-detail">{{ entry.summary }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</ng-template>



// WEBPACK FOOTER //
// ./src/app/portal/conversion/migration-progress-modal/migration-progress-modal.component.html